import { Component, OnInit, ViewChild } from '@angular/core'
import { Router } from '@angular/router'
import { NgForm } from '@angular/forms'
import { MatDialog, MatDialogConfig } from '@angular/material/dialog'
import { UntypedFormBuilder, FormControl, FormGroup } from '@angular/forms'

import { SiteService } from '../../../../services/site.service'
import { MessageService } from '../../../../services/message.service'
import { TranslateService } from '@ngx-translate/core'
import { CustomerRequestsService } from '../../../../services/customer-requests.service'

import { CustomerAccountTypes } from '../../../../enumeration'
import { BankAccountChangeViewModel } from '../../../../models/bankAccountChangeViewModel'
import { RequestSummaryBankAccountChangeModalComponent } from '../../../modals/request-summary-bank-account-change-modal/request-summary-bank-account-change-modal.component'
import { ErrorMessageModalComponent } from '../../../modals/error-message-modal/error-message-modal.component'

@Component({
    selector: 'app-request-bank-account-change-detail',
    templateUrl: './request-bank-account-change-detail.component.html',
    styleUrls: ['./request-bank-account-change-detail.component.scss'],
    standalone: false
})
export class RequestBankAccountChangeDetailComponent implements OnInit 
{
	@ViewChild('customForm') public customForm!: NgForm
	formSubmitted: boolean = false
	invalidVoidCheque: boolean = false
	effectiveDateIsValid: boolean = false
	showNewEffectiveDateNote: boolean = false
	isActiveLease: boolean = true
	voidChequeIsRequired: boolean = true

	institutionPattern = "^[0-9]{3,3}$"
	transitPattern = "^[0-9]{5,5}$"
	accountPattern = "^[0-9]{7,12}$"
	request =
	{
		institutionNumber: '',
		transitNumber: '',
		accountNumber: '',
	}
	voidChequeFile: any = undefined
	referenceNumber: string = ''
	bankAccountChangeViewModel = new BankAccountChangeViewModel()

	MAKE_LANDROVER: string = 'LAND ROVER'

	constructor(
		private router: Router,
		public dialog: MatDialog,
		private siteService: SiteService,
		private messageService: MessageService,
		private translateService: TranslateService,
		private formBuilder: UntypedFormBuilder,
		private customerRequestsService: CustomerRequestsService
	) 
	{ 
	}

	ngOnInit() 
	{
		this.retrieveBankAccountChangeViewModel()
	}

	retrieveBankAccountChangeViewModel()
	{
		this.messageService.startedHttpRequest('retrieveBankAccountChangeViewModel')
		this.customerRequestsService
			.getBankAccountChangeViewModel()
			.subscribe({
				next: (apiResponse) =>
				{
					this.bankAccountChangeViewModel = apiResponse

					if(this.bankAccountChangeViewModel && this.bankAccountChangeViewModel.isActiveLease)
					{
						let todayDateTime = new Date()
						if (new Date(this.bankAccountChangeViewModel.effectiveDate) > new Date(todayDateTime.toDateString()))
						{
							this.effectiveDateIsValid = true
						}

						this.showNewEffectiveDateNote = this.bankAccountChangeViewModel.isNewEffectiveDate
						
						this.voidChequeIsRequired = this.bankAccountChangeViewModel.make == this.MAKE_LANDROVER || 
												this.bankAccountChangeViewModel.customerAccountType == CustomerAccountTypes.SoleProprietorship ||
												this.bankAccountChangeViewModel.customerAccountType == CustomerAccountTypes.Partnership ||
												this.bankAccountChangeViewModel.customerAccountType == CustomerAccountTypes.Corporation ||
												this.bankAccountChangeViewModel.customerAccountType == CustomerAccountTypes.Municipality ||
												this.bankAccountChangeViewModel.customerAccountType == CustomerAccountTypes.NonProfitOrganization

						if(this.voidChequeIsRequired)
						{
							const uploadBtn = document.getElementById('voidChequeBtn');
							const fileInput = document.getElementById('voidChequeInput');

							if (uploadBtn && fileInput) {
								uploadBtn.addEventListener('click', () => {
									fileInput.click()
								})
							}
						}			
					}
					else
					{
						this.isActiveLease = false
					}
				},
				error: (error) =>
				{
					this.isActiveLease = false
					this.messageService.completedHttpRequest('retrieveBankAccountChangeViewModel')

					const dialogConfig = new MatDialogConfig()
					dialogConfig.width = '500px',
					dialogConfig.data = {
						title: this.translateService.instant('COMMON_LBL_ERROR'),
						message: this.translateService.instant('COMMON_LBL_ERROR_MSG'),
					}
					
					this.dialog.open(ErrorMessageModalComponent, dialogConfig)
				},
				complete: () =>
				{
					this.messageService.completedHttpRequest('retrieveBankAccountChangeViewModel')
				}
			})
	}

	handleFileInput(files: FileList) 
	{
		this.invalidVoidCheque = false
		this.voidChequeFile = files.item(0)

		if(this.voidChequeFile)
		{
			const allowedExtensions = ['pdf','bmp','jpeg','jpg', 'png', 'gif']
			const fileExtension = this.voidChequeFile.name.split('.').pop().toLowerCase()
	
			if (!allowedExtensions.includes(fileExtension)) 
			{
				this.invalidVoidCheque = true
			}
		}
	}
	
	submitForm(customerForm: NgForm)
	{
		this.formSubmitted = true

		if(this.voidChequeIsRequired)
		{
			this.validateFiles()
		}

		if (customerForm.invalid || this.invalidVoidCheque)
		{
			return
		}

		const requestSummaryList: { key: string; value: string }[] = [
			{ key: this.translateService.instant('REQUEST_BANK_ACCOUNT_CHANGE_CONFIRM_NAME'), value: this.siteService.customerAccount?.fullName ?? '' },
			{ key: this.translateService.instant('REQUEST_BANK_ACCOUNT_CHANGE_CONFIRM_LEASENUM'), value: this.bankAccountChangeViewModel.leaseNumber },
			{ key: this.translateService.instant('REQUEST_BANK_ACCOUNT_CHANGE_CONFIRM_INSTITUTION'), value: this.request.institutionNumber },
			{ key: this.translateService.instant('REQUEST_BANK_ACCOUNT_CHANGE_CONFIRM_TRANSIT'), value: this.request.transitNumber },
			{ key: this.translateService.instant('REQUEST_BANK_ACCOUNT_CHANGE_CONFIRM_ACCOUNT'), value: this.request.accountNumber }
		]

		if(this.effectiveDateIsValid)
		{
			const effectiveDate = new Date(this.bankAccountChangeViewModel.effectiveDate)
			requestSummaryList.push({ key: this.translateService.instant('REQUEST_BANK_ACCOUNT_CHANGE_CONFIRM_EFFECTIVE_DATE'), value: effectiveDate.getFullYear() + '/' + (effectiveDate.getMonth() + 1) + '/' + effectiveDate.getDate()})
		}

		return this.customerRequestsService.requestConfirmation(this.translateService.instant('REQUEST_BANK_ACCOUNT_CHANGE_CONFIRM_PRINT_TITLE'), requestSummaryList)
		.then((result) =>
		{
			if(result)
			{
				let formData = new FormData()
				formData.append('institutionNumber', this.request.institutionNumber)
				formData.append('transitNumber', this.request.transitNumber)
				formData.append('accountNumber', this.request.accountNumber)
				formData.append('voidChequeFile', this.voidChequeFile)
				formData.append('effectiveDate', this.bankAccountChangeViewModel.effectiveDate ? this.bankAccountChangeViewModel.effectiveDate.toString() : '')

				this.messageService.startedHttpRequest('submitForm')
				this.customerRequestsService
				.addBankAccountChangeRequest(formData)
				.subscribe({
					next: (apiResponse) =>
					{
						this.referenceNumber = apiResponse

						if ((this.referenceNumber != undefined) && (this.referenceNumber != ''))
						{
              				this.router.navigate(['/request-list'])

							return this.requestBankAccountChangeSummary(this.referenceNumber, this.translateService.instant('REQUEST_BANK_ACCOUNT_CHANGE_HEADING'), requestSummaryList)
							.then((result) =>
							{
								if(result)
								{
									this.customerRequestsService.extendLoginSession()
									this.router.navigate(['/address-change'])
								}
								else
								{
									this.siteService.logout()
								}								
							})		
						}	
													
						this.formSubmitted = false
					},
					error: (error) =>
					{
						this.messageService.completedHttpRequest('submitForm')

						const dialogConfig = new MatDialogConfig()
						dialogConfig.width = '500px',
						dialogConfig.data = {
							title: this.translateService.instant('COMMON_LBL_ERROR'),
							message: this.translateService.instant('COMMON_LBL_ERROR_MSG'),
						}
						
						this.dialog.open(ErrorMessageModalComponent, dialogConfig)
					},
					complete: () =>
					{
						this.messageService.completedHttpRequest('submitForm')
					}
				})
			}
		})	
	}

	requestBankAccountChangeSummary(referenceNumber: string, heading: string, requestSummaryList: any)
	{
		return new Promise((resolve, reject) =>
		{
			const dialogConfig = new MatDialogConfig()
			dialogConfig.width = '600px',
			dialogConfig.data = {
				referenceNumber: referenceNumber,
				heading: heading,
				requestSummaryList: requestSummaryList
			}
			
			const dialogRef =this.dialog.open(RequestSummaryBankAccountChangeModalComponent, dialogConfig)	
			dialogRef.afterClosed().subscribe(result => {
				resolve(result)
			})
		})
	}	

	validateFiles()
	{
		this.invalidVoidCheque = false

		if(this.voidChequeFile == undefined)
		{
			this.invalidVoidCheque = true
		}
		else
		{
			const allowedExtensions = ['pdf','bmp','jpeg','jpg', 'png', 'gif']
			const fileExtension = this.voidChequeFile.name.split('.').pop().toLowerCase()

			if (!allowedExtensions.includes(fileExtension)) 
			{
				this.invalidVoidCheque = true
			}
		}
	}

	institutionNumberValidator(event: any) 
	{
		const pattern = /^[0-9]*$/;   
		if (!pattern.test(event.target.value)) 
		{
			event.target.value = event.target.value.replace(/[^0-9]/g, "")
			this.request.institutionNumber = event.target.value
		}
	}

	transitNumberValidator(event: any) 
	{
		const pattern = /^[0-9]*$/;   
		if (!pattern.test(event.target.value)) 
		{
			event.target.value = event.target.value.replace(/[^0-9]/g, "")
			this.request.transitNumber = event.target.value
		}
	}

	accountNumberValidator(event: any) 
	{
		const pattern = /^[0-9]*$/;   
		if (!pattern.test(event.target.value)) 
		{
			event.target.value = event.target.value.replace(/[^0-9]/g, "")
			this.request.accountNumber = event.target.value
		}
	}
}
